import React, { FC, useState, useEffect } from 'react';
import { Grid, Button, Dialog, DialogActions, DialogTitle, DialogContent, Box, Alert, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LinkedUser, LinkedClinic, Order } from 'models/order';
import LinkedDentists from './linked-dentists.component';
import LinkedClinics from './linked-clinics.component';
import ParticipantsList from './participants-list.component';
import { emailRegExp } from 'config/regex';
import { User } from 'models/user';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client/react';
import { modifyParticipantsTemplate } from 'apollo/templates/order';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type ManageParticipantsDialogProps = {
    order?: Order;
    onAddParticipantsInOrderFlow?: (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => void;
    orderLinkedDentists?: LinkedUser[];
    orderLinkedClinics?: LinkedClinic[];
    orderCustomParticipantEmails?: string[];
    onClose: () => void;
    open: boolean;
};
const ManageParticipantsDialog: FC<ManageParticipantsDialogProps> = ({
    order,
    onAddParticipantsInOrderFlow,
    orderLinkedDentists,
    orderLinkedClinics,
    orderCustomParticipantEmails,
    onClose,
    open,
}: ManageParticipantsDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices', 'user-settings']);
    const [linkedDentists, setLinkedDentists] = useState<LinkedUser[]>([]);
    const [linkedClinics, setLinkedClinics] = useState<LinkedClinic[]>([]);
    const [customEmails, setCustomEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyParticipants] = useMutation(modifyParticipantsTemplate, {
        onCompleted: () => {
            setLoading(false);
            onClose();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (orderLinkedDentists) {
            setLinkedDentists(orderLinkedDentists);
        }
        if (orderLinkedClinics) {
            setLinkedClinics(orderLinkedClinics);
        }
        if (orderCustomParticipantEmails) {
            setCustomEmails(orderCustomParticipantEmails);
            setIsValidEmail(true);
        }
    }, [orderLinkedDentists, orderLinkedClinics, orderCustomParticipantEmails]);

    const handleAddDentists = (linkedDentists: LinkedUser[]) => {
        setLinkedDentists(linkedDentists);
    };

    const handleAddClinics = (linkedClinics: LinkedClinic[]) => {
        setLinkedClinics(linkedClinics);
    };

    const validateEmail = (value: string) => {
        const email = emailRegExp.test(String(value).toLowerCase());
        setIsValidEmail(email);
        return email;
    };

    const handleToError = (err: Error | null) => {
        setIsValidEmail(!err);
    };

    const handleEmailInput = (value: string[]) => {
        setCustomEmails(value);
    };

    const handleRemoveLinkedDentist = (dentist: User) => {
        if (linkedDentists) {
            setLinkedDentists(linkedDentists.filter((linkedDentist) => linkedDentist.user._id !== dentist._id));
        }
    };

    const handleRemoveLinkedClinic = (clinic: Organization) => {
        if (linkedClinics) {
            setLinkedClinics(linkedClinics.filter((linkedClinic) => linkedClinic.clinic._id !== clinic._id));
        }
    };

    const handleClose = () => {
        onClose();
        setIsValidEmail(false);
        setLoading(false);
        setLinkedDentists(orderLinkedDentists || []);
        setLinkedClinics(orderLinkedClinics || []);
        setCustomEmails(orderCustomParticipantEmails || []);
    };

    const handleAddParticipants = async () => {
        if (order?._id) {
            setLoading(true);
            const userIds = linkedDentists.map((linkedDentist) => linkedDentist.user._id);
            const clinicIds = linkedClinics.map((linkedClinic) => linkedClinic.clinic._id);
            const participants = {
                linkedDentists: userIds,
                linkedClinics: clinicIds,
                customParticipantEmails: customEmails,
            };
            await modifyParticipants({ variables: { orderId: order._id, participants: participants } });
        } else {
            if (onAddParticipantsInOrderFlow) {
                onAddParticipantsInOrderFlow(linkedDentists, linkedClinics, customEmails);
            }
        }
    };

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
            <DialogTitle>{translate('orders:MANAGE_PARTICIPANTS')}</DialogTitle>
            <DialogContent sx={{ minHeight: 200 }}>
                {loading ? <DelayedLinearProgress /> : undefined}
                <Box mb={2}>
                    <Alert severity="info">{translate('orders:PARTICIPANT_INFO')}</Alert>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <LinkedDentists orderLinkedDentists={linkedDentists} onAddDentists={handleAddDentists} />
                        </Box>
                        <Box mt={2}>
                            <LinkedClinics orderLinkedClinics={linkedClinics} onAddClinics={handleAddClinics} />
                        </Box>
                        <Box mt={2}>
                            <ChipInput
                                label={translate('user-settings:EMAIL_ADDRESSES')}
                                placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                createChipOnBlur
                                options={[]}
                                chipValidator={validateEmail}
                                onError={handleToError}
                                defaultValue={customEmails || []}
                                onValueChange={(value: string[]) => handleEmailInput(value)}
                                onDelete={(value: string[]) => handleEmailInput(value)}
                            />
                            <Box mt={0.5}>
                                <InfoOutlinedIcon sx={{ verticalAlign: 'middle' }} color="info" />
                                <Typography color="info.light" variant="caption" sx={{ marginLeft: 0.5 }}>
                                    {translate('invoices:EMAIL_PLACEHOLDER')}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ParticipantsList
                            showRemoveButton={true}
                            linkedDentists={linkedDentists}
                            linkedClinics={linkedClinics}
                            customEmails={customEmails}
                            onRemoveDentist={handleRemoveLinkedDentist}
                            onRemoveClinic={handleRemoveLinkedClinic}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{translate('CLOSE')}</Button>
                <Button
                    variant="contained"
                    disabled={customEmails.length > 0 && !isValidEmail}
                    onClick={handleAddParticipants}
                >
                    {translate('SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageParticipantsDialog;
